/**
 * client/src/features/income/services/api.js
 * 
 * API utility helpers for development and debugging.
 * Provides mock API responses and testing utilities.
 */

// Track API calls for debugging
let apiCalls = [];

/**
 * Log an API call for debugging
 * @param {string} method - HTTP method
 * @param {string} endpoint - API endpoint
 * @param {Object} data - Request data (optional)
 * @param {Object} response - Response data (optional)
 */
const logApiCall = (method, endpoint, data = null, response = null) => {
  const call = {
    timestamp: new Date().toISOString(),
    method,
    endpoint,
    data,
    response,
    success: true
  };
  
  apiCalls.unshift(call); // Add to beginning
  
  // Keep only the last 50 calls
  if (apiCalls.length > 50) {
    apiCalls = apiCalls.slice(0, 50);
  }
  
  console.log(`📡 API ${method} ${endpoint}`, { data, response });
  return call;
};

/**
 * Log an API error for debugging
 * @param {string} method - HTTP method
 * @param {string} endpoint - API endpoint
 * @param {Object} data - Request data (optional)
 * @param {Error} error - Error object
 */
const logApiError = (method, endpoint, data = null, error = null) => {
  const call = {
    timestamp: new Date().toISOString(),
    method,
    endpoint,
    data,
    error: error?.message || 'Unknown error',
    success: false
  };
  
  apiCalls.unshift(call);
  
  // Keep only the last 50 calls
  if (apiCalls.length > 50) {
    apiCalls = apiCalls.slice(0, 50);
  }
  
  console.error(`❌ API Error: ${method} ${endpoint}`, { data, error });
  return call;
};

/**
 * Get all logged API calls
 * @returns {Array} Array of API calls
 */
const getApiCalls = () => apiCalls;

/**
 * Clear all logged API calls
 */
const clearApiCalls = () => {
  apiCalls = [];
  return 'API calls cleared';
};

/**
 * Test localStorage access
 * @returns {Object} Test result
 */
const testStorage = () => {
  try {
    const testKey = 'theWallet_api_test';
    localStorage.setItem(testKey, JSON.stringify({ test: true }));
    const value = localStorage.getItem(testKey);
    localStorage.removeItem(testKey);
    
    return {
      success: true,
      message: 'LocalStorage test successful',
      value
    };
  } catch (err) {
    return {
      success: false,
      message: `LocalStorage test failed: ${err.message}`,
      error: err
    };
  }
};

/**
 * Mock API responses for development
 * @param {string} endpoint - API endpoint
 * @param {Object} mockData - Mock data to return
 * @returns {Promise} Promise that resolves with the mock data
 */
const mockResponse = (endpoint, mockData) => {
  return new Promise(resolve => {
    setTimeout(() => {
      logApiCall('GET', endpoint, null, mockData);
      resolve({ data: mockData });
    }, Math.random() * 300 + 100);
  });
};

// Expose debug helpers in the browser console
if (typeof window !== 'undefined') {
  window.apiDebug = {
    getApiCalls,
    clearApiCalls,
    testStorage,
    mockBucket: (name = 'Test Bucket', type = 'HOUSEHOLD') => {
      return {
        _id: `mock_${Date.now()}`,
        name,
        type,
        isExpanded: true,
        order: 999,
        createdAt: new Date().toISOString()
      };
    }
  };
}

export { 
  logApiCall, 
  logApiError, 
  getApiCalls, 
  clearApiCalls, 
  testStorage, 
  mockResponse 
}; 